import { usePopover } from '@sorrem-client/ui/components';
import { useCallback, useEffect, useRef, type ForwardedRef, type MutableRefObject, type ReactNode, type RefCallback } from 'react';

/** How long the pointer rests on an element before its popover opens, so sweeping across a row of slots stays quiet. */
const HOVER_POPOVER_DELAY_MS = 250;

/** Spread onto the element the popover describes. */
export interface HoverPopoverBinding {
    ref: RefCallback<HTMLDivElement>;
    onMouseEnter: (() => void) | undefined;
    onMouseLeave: () => void;
    onPointerDownCapture: () => void;
}

/**
 * Shows `content` in the app's popover above an element while the pointer
 * rests on it, e.g. what a loadout frame holds. The open popover follows
 * `content` as it changes, and closes when the pointer leaves, the element is
 * pressed (to use, drag or place something), `content` empties, or the
 * element unmounts. Nothing shows while `content` is null or undefined.
 *
 * @param forwardedRef A ref the element also forwards, for components built with `forwardRef`.
 */
export function useHoverPopover(content: ReactNode, forwardedRef?: ForwardedRef<HTMLDivElement>): HoverPopoverBinding {
    const { triggerRef, showPopover, hidePopover, isPopoverVisible } = usePopover({ delay: HOVER_POPOVER_DELAY_MS, placement: 'top' });
    const hasContent = content !== undefined && content !== null;
    const isPopoverVisibleRef = useRef(isPopoverVisible);
    useEffect(() => {
        isPopoverVisibleRef.current = isPopoverVisible;
    }, [isPopoverVisible]);

    // Keep an open popover current as what it describes changes under the
    // pointer (a stack used up, a crystal formed), and close it once empty.
    useEffect(() => {
        if (!isPopoverVisibleRef.current) {
            return;
        }
        if (hasContent) {
            showPopover(content);
        } else {
            hidePopover();
        }
    }, [content, hasContent, hidePopover, showPopover]);

    // An element that goes away (the loadout hidden, a panel closed) takes its popover with it.
    useEffect(() => (): void => {
        if (isPopoverVisibleRef.current) {
            hidePopover();
        }
    }, [hidePopover]);

    const ref = useCallback((node: HTMLDivElement | null): void => {
        // The popover's trigger ref is a plain `useRef`; it anchors to whichever node is set here.
        (triggerRef as MutableRefObject<HTMLDivElement | null>).current = node;
        if (typeof forwardedRef === 'function') {
            forwardedRef(node);
        } else if (forwardedRef) {
            forwardedRef.current = node;
        }
    }, [forwardedRef, triggerRef]);

    return {
        ref,
        onMouseEnter: hasContent ? (): void => showPopover(content) : undefined,
        onMouseLeave: hidePopover,
        onPointerDownCapture: hidePopover,
    };
}
